import { ethers } from 'ethers'
import { getCurrentNetwork } from './ethereum-config'
import NFTOTEM_ABI from '@/lib/abi/nftotem'

export type AuthAction = 'create-ownership' | 'update-ownership'

export function buildAuthMessage(params: {
  action: AuthAction
  walletAddress: string
  issuedAt: number // unix ms
  tokenId?: number | null
}): string {
  const { action, walletAddress, issuedAt, tokenId } = params
  const normalized = walletAddress.toLowerCase()
  const tokenStr = typeof tokenId === 'number' && tokenId > 0 ? String(tokenId) : 'N/A'
  return [
    'NFTotem Signature',
    `Action: ${action}`,
    `Address: ${normalized}`,
    `TokenId: ${tokenStr}`,
    `IssuedAt: ${issuedAt}`
  ].join('\n')
}

export function verifyAddressSignature(params: {
  message: string
  signature: string
  walletAddress: string
}): boolean {
  try {
    const recovered = ethers.verifyMessage(params.message, params.signature)
    return recovered?.toLowerCase?.() === params.walletAddress.toLowerCase()
  } catch (e) {
    return false
  }
}

export async function ensureOnchainOwnership(params: {
  walletAddress: string
  tokenId?: number | null
}): Promise<{ ownsToken: boolean; tokenId?: number | null }> {
  const { walletAddress, tokenId } = params
  try {
    const { getCurrentProvider } = await import('./ethereum-config')
    const provider = getCurrentProvider()
    const network = getCurrentNetwork()
    const contract = new ethers.Contract(network.contractAddress, NFTOTEM_ABI, provider)

    // Quick balance check
    const balance = await contract.balanceOf(walletAddress)
    const hasMinted = Number(balance) > 0
    if (!hasMinted) return { ownsToken: false }

    if (typeof tokenId === 'number' && tokenId > 0) {
      try {
        const owner = await contract.ownerOf(tokenId)
        const owns = owner?.toLowerCase?.() === walletAddress.toLowerCase()
        return { ownsToken: owns, tokenId }
      } catch {
        return { ownsToken: false }
      }
    }

    // Resolve tokenId by iterating (contract starts at 1)
    try {
      const totalSupply = await contract.totalSupply()
      const total = Number(totalSupply)
      for (let i = 1; i <= total; i++) {
        try {
          const owner = await contract.ownerOf(i)
          if (owner?.toLowerCase?.() === walletAddress.toLowerCase()) {
            return { ownsToken: true, tokenId: i }
          }
        } catch {
          continue
        }
      }
      // If we did not find ownership, return false strictly
      return { ownsToken: false }
    } catch {
      return { ownsToken: false }
    }
  } catch {
    return { ownsToken: false }
  }
}


